import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Mail, MessageCircle } from "lucide-react";
import { CLINIC, whatsappLink, mailtoLink } from "@/lib/clinic";

export default function Contact() {
    return (
        <section
            id="contact"
            className="py-24 md:py-32"
            data-testid="contact-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <span className="text-xs uppercase tracking-[0.3em] text-[#8A9A5B] font-medium">
                        — Visit Us
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#1A221F] mt-4 leading-[1.1] tracking-tight">
                        Find us in the heart of <em className="italic text-[#3B5249]">Amravati.</em>
                    </h2>
                </motion.div>

                <div className="grid lg:grid-cols-5 gap-8 mt-16">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-2 flex flex-col gap-4"
                    >
                        <InfoCard
                            icon={MapPin}
                            label="Address"
                            value={CLINIC.address}
                            action={{
                                href: CLINIC.mapsLink,
                                label: "Open in Maps",
                                testId: "contact-maps-link",
                            }}
                            testId="contact-address"
                        />
                        <InfoCard
                            icon={Phone}
                            label="Phone"
                            value={CLINIC.phone}
                            action={{
                                href: `tel:${CLINIC.phoneRaw}`,
                                label: "Call now",
                                testId: "contact-call-link",
                            }}
                            testId="contact-phone"
                        />
                        <InfoCard
                            icon={Clock}
                            label="Hours"
                            value={`Every day · ${CLINIC.hours}`}
                            testId="contact-hours"
                        />
                        <InfoCard
                            icon={Mail}
                            label="Email"
                            value={CLINIC.email}
                            action={{
                                href: mailtoLink(),
                                label: "Send email",
                                testId: "contact-mailto-link",
                            }}
                            testId="contact-email"
                        />

                        <a
                            href={whatsappLink()}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-4 text-sm font-medium hover:bg-[#1EBE5D] transition-all hover:scale-[1.02]"
                            data-testid="contact-whatsapp-button"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Chat with us on WhatsApp
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-3 rounded-[2rem] overflow-hidden border border-[#E8E6E1] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] min-h-[460px]"
                        data-testid="contact-map"
                    >
                        <iframe
                            src={CLINIC.mapsEmbed}
                            title="Mirrari Clinic location"
                            width="100%"
                            height="100%"
                            style={{ border: 0, minHeight: 460 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function InfoCard({ icon: Icon, label, value, action, testId }) {
    return (
        <div
            className="bg-white rounded-3xl p-6 border border-[#E8E6E1] shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
            data-testid={testId}
        >
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-2xl bg-[#F3F1EC] flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#3B5249]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#7A8C84]">
                        {label}
                    </div>
                    <div className="text-[#1A221F] mt-1 text-sm leading-relaxed">
                        {value}
                    </div>
                    {action && (
                        <a
                            href={action.href}
                            target={action.href.startsWith("http") ? "_blank" : undefined}
                            rel="noreferrer"
                            className="inline-block mt-2 text-xs text-[#3B5249] border-b border-[#3B5249]/30 hover:border-[#3B5249] pb-0.5 transition-colors"
                            data-testid={action.testId}
                        >
                            {action.label} →
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}
